import React, { useState, type ComponentProps } from 'react';
import { Box, Typography, Breadcrumbs, Link, Button, Paper, Stack, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import type { ReactNode } from 'react';
import { Assignment as LogsIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { SftpSystemSettings } from './SftpSystemSettings';
import { SftpTenantConfigGrid } from './SftpTenantConfigGrid';
import { SftpTenantConfigDialog } from './SftpTenantConfigDialog';
import { toCloneValues } from './helpers/sftpSecretInputs';
import { useDeleteSftpTenantConfigMutation } from '@/services/graphql';
import { showMutationErrors, handleMutationSuccess } from '@/utils/errorHandling';
import type { SftpTenantConfigDocument } from '@/services/graphql';

type SftpTenantConfig = SftpTenantConfigDocument;

interface RibbonButtonProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

const RibbonButton: React.FC<RibbonButtonProps> = ({ icon, label, onClick, disabled }) => (
  <Button
    disabled={disabled}
    onClick={onClick}
    size="small"
    variant="text"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 'auto',
      height: 'auto',
      px: 1.5,
      py: 0.5,
      textTransform: 'none',
      color: 'text.primary',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      border: 0,
      '&:hover': { backgroundColor: 'action.hover' },
    }}
  >
    <Stack alignItems="center" direction="column" spacing={0.5}>
      {icon}
      <Typography sx={{ fontSize: '.85em', fontWeight: 400 }}>{label}</Typography>
    </Stack>
  </Button>
);

export const SftpConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedConfig, setSelectedConfig] = useState<SftpTenantConfig | null>(null);
  const [shouldRefreshGrid, setShouldRefreshGrid] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<SftpTenantConfig | null>(null);
  const [cloneValues, setCloneValues] = useState<ComponentProps<typeof SftpTenantConfigDialog>['initialValues']>();

  const [deleteConfig] = useDeleteSftpTenantConfigMutation();

  const handleViewLogs = () => {
    navigate('/sftp-sync/logs?entity=SFTP_SYNCHRONIZATION');
  };

  const handleCreateConfig = () => {
    setCloneValues(undefined);
    setDialogMode('create');
    setSelectedConfig(null);
    setDialogOpen(true);
  };

  const handleEditConfig = (config: SftpTenantConfig) => {
    setDialogMode('edit');
    setSelectedConfig(config);
    setDialogOpen(true);
  };

  const handleCloneConfig = (config: SftpTenantConfig) => {
    setCloneValues(toCloneValues(config));
    setDialogMode('create');
    setSelectedConfig(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedConfig(null);
  };

  const handleDialogSuccess = () => {
    // Close dialog and trigger grid refresh
    setDialogOpen(false);
    setSelectedConfig(null);
    setShouldRefreshGrid(prev => prev + 1);
  };

  const handleDeleteConfig = (config: SftpTenantConfig) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!configToDelete) return;

    try {
      const result = await deleteConfig({
        input: { configId: configToDelete.configId },
      }).unwrap();

      if (result?.deleteSftpTenantConfig?.sftpTenantConfig) {
        handleMutationSuccess('Configuration deleted successfully');
        setShouldRefreshGrid(prev => prev + 1);
      } else if (result?.deleteSftpTenantConfig?.errors?.length) {
        showMutationErrors({ 
          errors: result.deleteSftpTenantConfig.errors || undefined 
        });
      }
    } catch (err) {
      console.error('Failed to delete configuration:', err);
    } finally {
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
  };

  return (
    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" href="/">
          Home
        </Link>
        <Link underline="hover" color="inherit" href="/sftp-sync">
          SFTP Sync
        </Link>
        <Typography color="text.primary">Configuration</Typography>
      </Breadcrumbs>

      {/* System Settings Section */}
      <Box sx={{ mb: 3 }}>
        <SftpSystemSettings />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Tenant Configurations Section */}
      <Paper sx={{ height: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column' }}>
        {/* Ribbon toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 80,
            px: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            flexWrap: 'nowrap',
            gap: 0,
          }}
        >
          <RibbonButton icon={<AddIcon />} label="Add Config" onClick={handleCreateConfig} />
          <RibbonButton icon={<LogsIcon />} label="View Logs" onClick={handleViewLogs} />
        </Box>

        {/* Grid */}
        <Box sx={{ flex: 1 }}>
          <SftpTenantConfigGrid
            key={shouldRefreshGrid}
            height="100%"
            onCreateConfig={handleCreateConfig}
            onEditConfig={handleEditConfig}
            onCloneConfig={handleCloneConfig}
            onDeleteConfig={handleDeleteConfig}
          />
        </Box>
      </Paper>

      {/* Config Dialog */}
      {dialogOpen && (
        <SftpTenantConfigDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSuccess={handleDialogSuccess}
          onDelete={dialogMode === 'edit' && selectedConfig ? () => {
            handleDeleteConfig(selectedConfig);
            handleDialogClose();
          } : undefined}
          mode={dialogMode}
          configId={selectedConfig?.configId}
          initialValues={dialogMode === 'create' ? cloneValues : undefined}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete SFTP Configuration
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the SFTP configuration "{configToDelete?.configName}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};